<template>
  <SidebarLayout2
    :breadcrumb="breadcrumb"
    pageTitle="Calendário de tarifas e disponibilidade"
    class="allotment-page"
  >
    <template #sidebar>
      <!-- NOME DO HOTEL -->
      <SidebarBlock class="allotment-page-sidebar--hotel">
        <Paragraph class="allotment-page-sidebar--hotel-text">
          {{ name }}
        </Paragraph>
      </SidebarBlock>

      <!-- MENU DOS HOTÉIS -->
      <SidebarBlock>
        <PmwHotelNav
          :links="menuLinks"
          :hide="['banners']"
          active="allotment"
          class="allotment-page-sidebar--menu"
        />
      </SidebarBlock>

      <SidebarBlock
        v-if="showReturnButton"
        class="allotment-page-sidebar--actions"
      >
        <Paragraph fontSize="large" fontWeight="medium">
          Ações da página
        </Paragraph>

        <PmwButton
          link="/hoteis"
          element="router-link"
          layout="outline"
          variation="blue-100"
        >
          Voltar para lista de hotéis
        </PmwButton>

        <PmwButton
          v-if="getIsAdmin"
          :disabled="baseRates.length < 2"
          :tooltipText="
            baseRates.length < 2
              ? 'É necessário ter pelo menos duas tarifas base para poder realizar a exclusão'
              : ''
          "
          layout="outline"
          variation="blue-100"
          @click="openDeleteBaseRateModal"
        >
          Excluir tarifa base
        </PmwButton>
      </SidebarBlock>
    </template>

    <template #page-description>
      <Paragraph
        fontSize="x-large"
        fontWeight="light"
        class="allotment-page--description"
      >
        {{ name }}
      </Paragraph>
    </template>

    <!-- CONTEÚDO PRINCIPAL -->
    <template #content>
      <div class="allotment-page--content">
        <!-- FILTROS FIXOS -->
        <b-row>
          <!-- QUARTO -->
          <b-col cols="2">
            <FormSelect
              v-model="selectedRoom"
              :options="roomsOptions"
              label="Quartos"
              capitalize
            />
          </b-col>

          <!-- TARIFA - só aparece para tarifa absoluta -->
          <b-col
            v-if="
              isAbsoluteRate &&
                ratesOptions.length > 1 &&
                selectedRoom.value !== 0
            "
            cols="2"
          >
            <FormSelect v-model="rate" :options="ratesOptions" label="Tarifa" />
          </b-col>

          <!-- OCUPAÇÃO -->
          <b-col v-if="selectedRoom.value !== 0" cols="2">
            <FormSelect
              v-model="ocupation"
              :options="selectedRoomPaxOptions"
              label="Ocupação"
            />
          </b-col>

          <!-- PERÍODO INICIAL -->
          <b-col cols="2">
            <FormSelect
              v-model="startPeriod"
              :options="startPeriodOptions"
              label="Período inicial"
              capitalize
            >
              <template #extra-content>
                <Paragraph fontSize="small" class="allotment-page--input-max"
                  >Você pode cadastrar uma tarifa com um período máximo de
                  <strong>{{ hotelMaxMonth }} meses</strong>
                  no futuro.</Paragraph
                >
              </template>
            </FormSelect>
          </b-col>

          <!-- PERÍODO MOSTRADO -->
          <b-col cols="3">
            <FormField label="Período mostrado">
              <b-row class="mt-3">
                <b-col class="radio-fields">
                  <InputRadio
                    v-model="period"
                    :inputValue="1"
                    :checked="period === 1"
                    label="1 Mês"
                    name="period"
                    class="radio-fields--input"
                  />
                  <InputRadio
                    v-if="showMaxMonths"
                    v-model="period"
                    :inputValue="MaxMonthValue"
                    :checked="period === MaxMonthValue"
                    :label="maxMonthsLabel"
                    name="period"
                    class="radio-fields--input"
                  />
                </b-col>
              </b-row>
            </FormField>
          </b-col>
        </b-row>

        <Paragraph fontSize="small" class="allotment-page--max-months"
          >Você pode cadastrar uma tarifa com um período máximo de
          {{ hotelMaxMonth }} meses no futuro.</Paragraph
        >

        <!-- LINHA DIVISÓRIA -->
        <b-row>
          <b-col>
            <SeparateLine :left="0" :right="0" :top="15" />
          </b-col>
        </b-row>

        <!-- LEGENDA -->
        <b-row class="align-items-end">
          <b-col>
            <!-- Legenda de bolinha -->
            <FormField label="Legenda">
              <b-row class="mt-2">
                <b-col cols="2">
                  <Paragraph
                    class="circle-legend circle-legend__opened"
                    fontSize="x-small"
                  >
                    Aberto para vendas
                  </Paragraph>
                </b-col>

                <b-col cols="auto">
                  <Paragraph
                    class="circle-legend circle-legend__closed"
                    fontSize="x-small"
                  >
                    Fechado para vendas
                  </Paragraph>
                </b-col>

                <b-col cols="auto">
                  <Paragraph
                    v-if="selectedRoom.value === 0"
                    class="circle-legend circle-legend__parcial-closed"
                    fontSize="x-small"
                  >
                    Fechado parcialmente
                  </Paragraph>
                </b-col>
              </b-row>
            </FormField>
          </b-col>

          <!-- MENU OPCIONAL -->
          <b-col cols="1" class="col__self-end">
            <Dropdown class="optional-filters" position="bottom-end">
              <template #button-content
                ><Icon icon="gear" class="optional-filters--icon"
              /></template>

              <!-- OPÇÕES -->
              <div class="optional-filters-options">
                <!-- MIN NOITES -->
                <div class="options-section">
                  <InputCheckbox
                    v-model="minNights"
                    :disabled="selectedRoom.value === 0"
                    :checked="selectedRoom.value !== 0"
                    label="Mín. noites"
                    name="min-noites"
                    class="options-section--option"
                  />
                </div>

                <!-- DISPONIBILIDADE -->
                <div class="options-section">
                  <InputRadio
                    v-model="disponibility"
                    :disabled="selectedRoom.value === 0"
                    :inputValue="'fechado-in-out'"
                    :checked="false"
                    label="Fechado para check in/out"
                    name="disponibility"
                    class="options-section--option"
                  />
                  <InputRadio
                    v-model="disponibility"
                    :inputValue="'letsbook'"
                    :checked="true"
                    label="Disponibilidade Let’s Book"
                    name="disponibility"
                    class="options-section--option"
                  />
                  <InputRadio
                    v-model="disponibility"
                    :inputValue="'vendido'"
                    :checked="false"
                    label="Vendido"
                    name="disponibility"
                    class="options-section--option"
                  />
                </div>

                <!-- DESCONTO E VARIAÇÃO (SOMENTE PARA FLUTUANTE) - Somente na V2, manter comentado -->
                <!-- <div class="options-section">
                  <InputRadio
                    v-model="discountVariation"
                    :inputValue="'desconto'"
                    :checked="false"
                    label="Desconto aplicado"
                    name="desc-variacao"
                    class="options-section--option"
                  />
                  <InputRadio
                    v-model="discountVariation"
                    :inputValue="'variacao'"
                    :checked="true"
                    label="Variação em % sobre a base"
                    name="desc-variacao"
                    class="options-section--option"
                  />
                </div> -->
              </div>
            </Dropdown>
          </b-col>
        </b-row>

        <!-- CALENDÁRIO -->
        <b-row class="mt-4">
          <b-col>
            <RatesAndAllotmentCalendar
              :calendarPeriod="calendarPeriod"
              :calendarAttributes="calendarAttributes"
              :compressCalendarHeight="compressCalendarHeight"
              :disponibility="disponibility"
              :minNights="minNights"
              :loading="loading"
              @showDayEdition="getDayEditionData"
            />
          </b-col>
        </b-row>

        <!-- MODAL EDIÇÃO -->
        <DayEditionModal
          v-model="showEditDayModal"
          :dayData="editionModalCurrentData"
          :rate="rate.value"
          :hotel="name"
          @savedData="updateDaily"
        />

        <!-- MODAL EXCLUSÃO DE TARIFA BASE -->
        <DeleteBaseRateModal
          v-model="showDeleteBaseRateModal"
          :rates="baseRates"
          :hotelName="name"
          ref="deleteBaseRateModal"
          @delete="confirmDeleteBaseRate"
        />
      </div>
    </template>
  </SidebarLayout2>
</template>

<script>
import { addMonths, endOfMonth } from 'date-fns'
import { mapActions, mapGetters } from 'vuex'
import { formatDate } from '@/helpers/date'
import { capitalizeString } from '@/helpers/string'
import SidebarLayout2 from '@/layouts/SidebarLayout2'
import hotelCommonsMixin from '@/mixins/hotelCommonsMixin'
import FormSelect from '@/components/_base/elements/FormSelect'
import FormField from '@/components/_base/patterns/FormField'
import InputRadio from '@solucoes/components-lib-tmp/src/components/elements/InputRadio'
import InputCheckbox from '@solucoes/components-lib-tmp/src/components/elements/InputCheckbox'
import Paragraph from '@/components/_base/elements/Paragraph'
import SeparateLine from '@/components/_base/patterns/SeparateLine'
import Dropdown from '@/components/_base/patterns/Dropdown'
import DayEditionModal from '@/components/hotel/allotmentAndRatesCalendar/DayEditionModal'
import DeleteBaseRateModal from '@/components/hotel/allotmentAndRatesCalendar/DeleteBaseRateModal'
import RatesAndAllotmentCalendar from '@/components/hotel/allotmentAndRatesCalendar/Calendar'
import SidebarBlock from '@solucoes/components-lib-tmp/src/components/patterns/SidebarBlock'
import PmwButton from '@solucoes/components-lib-tmp/src/components/elements/PmwButton'

export default {
  name: 'AllotmentAndRatesCalendar',
  mixins: [hotelCommonsMixin],
  components: {
    SidebarLayout2,
    FormSelect,
    SeparateLine,
    InputRadio,
    InputCheckbox,
    FormField,
    Paragraph,
    Dropdown,
    DayEditionModal,
    DeleteBaseRateModal,
    RatesAndAllotmentCalendar,
    SidebarBlock,
    PmwButton
  },
  data() {
    /**
     * Pega a data atual para iniciar a criação das
     * opções do select de período inicial
     * * Data atual + 12 meses
     */
    const startTimeOptions = []
    const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' })

    for (let i = 0; i < 12; i++) {
      const date = addMonths(new Date(), i)
      const month = date.getMonth() + 1
      const year = date.getFullYear()
      const index = i

      startTimeOptions.push({
        text: `${formatter.format(date)} ${year}`,
        value: { month, year },
        index: { index }
      })
    }

    return {
      name: '' /** Nome do Hotel */,
      selectedRoom: '' /** Quarto selecionado */,
      rate: '' /** Tarifa selecionada */,
      ocupation: '' /** Quantidade de hóspedes */,
      startPeriod: startTimeOptions[0] /** Mês inicial do calendário */,
      period: 1 /** Quantidade de meses mostrados. 1 ou 6 */,
      minNights: true /** Mostra o mínimo de noites */,
      disponibility: 'letsbook' /** Filtro de disponibilidade */,
      discountVariation:
        'variacao' /** Filtro de desconto e variação (tarifa flutuante) */,
      rooms: [] /** Quartos do hotel */,
      showEditDayModal: false /** Status de visualização da modal de edição */,
      editionModalCurrentData: {
        title: ''
      },
      /** Caminho de pão */
      breadcrumb: [],
      filterRequestDelay: 1000 /** Delay entre as requisições  do filtro */,
      filterRequest: null /** REquisição do filtro que está para ser executada */,
      compressCalendarHeight: false /** Se deve exibir o calendario comprimido */,
      ratesOptions: [] /** Opções de tarifas quando o hotel for absoluto */,
      startPeriodOptions: startTimeOptions /** Data inicial do período */,
      calendarData: [] /** Dados do calendário de tarifas */,
      loading: true /** Se está carregando os dados do calendeário */,
      hotelMaxMonth: null /** Quantidade de meses máxima do hotel */,
      maxMonthsLabel: null /** Label do período mostrado */,
      showMaxMonths: true /** Controla a visualização do Máx. período mostrado */,
      MaxMonthValue: null /** Controla valor do Máx. período mostrado */,
      showTooltip: false,
      baseRates: [] /** Lista de tarifas base para exclusão */,
      showDeleteBaseRateModal: false /** Status de visualização da modal de exclusão de tarifa base */
    }
  },
  computed: {
    /** Atributos formatados no padrão do calendário */
    calendarAttributes() {
      return this.calendarData
    },
    /** Período padronizado para o calendário */
    calendarPeriod() {
      const startDate = new Date(
        this.startPeriod.value.year,
        this.startPeriod.value.month - 1,
        1,
        0,
        0,
        0,
        0
      )
      const endDate =
        +this.period > 1
          ? addMonths(startDate, +this.period - 1)
          : endOfMonth(startDate)

      return {
        rawStart: startDate,
        start: formatDate(startDate, 'yyyy-MM-dd'),
        rawEnd: endDate,
        end: formatDate(endDate, 'yyyy-MM-dd')
      }
    },
    /** Se está buscando 1 mês */
    oneMonth() {
      return +this.period === 1
    },
    /** Retorna as opções de quartos ativos no formato do componente */
    roomsOptions() {
      return this.rooms.map((item) => ({
        text: item.nome,
        value: item.codigo
      }))
    },
    /**
     * Retorna as possíveis opções de pax
     * do quarto selecionado
     */
    selectedRoomPaxOptions() {
      const options = []
      const found = this.rooms.find(
        (item) => item.codigo === this.selectedRoom.value
      )

      if (found) {
        /** ADULTOS */
        for (let i = 1; i <= found.maximoAdultos; i++) {
          const obj = {
            text: `${i} ${this.$options.filters.pluralizer(
              i,
              'Adulto | Adultos',
              true
            )}`,
            value: {
              type: 10,
              quantity: i
            }
          }

          options.push(obj)
        }

        /** CRIANÇAS */
        for (let i = 1; i <= found.maximoCriancas; i++) {
          const obj = {
            text: `${i} ${this.$options.filters.pluralizer(
              i,
              'Criança | Crianças',
              true
            )}`,
            value: {
              type: 8,
              quantity: i
            }
          }

          options.push(obj)
        }
      }

      return options
    },

    /** Exibe o botão de voltar para o hotel apenas se o usuário tiver permissão */
    showReturnButton() {
      return this.menuLinks.length > 0
    },

    ...mapGetters('hotel', ['isAbsoluteRate', 'getCurrency']),
    ...mapGetters('user', ['getIsAdmin'])
  },
  watch: {
    /** Recebe quantidade de meses do hotel para atualizar o select */
    hotelMaxMonth(newValue) {
      const startTimeOptions = []
      const formatter = new Intl.DateTimeFormat('pt-BR', { month: 'long' })

      for (let i = 0; i < newValue + 1; i++) {
        const date = addMonths(new Date(), i)
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        const index = i

        startTimeOptions.push({
          text: `${formatter.format(date)} ${year}`,
          value: { month, year },
          index: { index }
        })
      }

      this.startPeriod = startTimeOptions[0]
      this.startPeriodOptions = startTimeOptions
    },
    selectedRoom(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      /** Seleciona a primeira opção de ocupação do quarto selecionado */
      this.ocupation = this.selectedRoomPaxOptions[0]

      this.getCalendarData()
    },
    rate(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    },
    ocupation(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    },
    startPeriod(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      /** Salva valor do Index do item selecionado */
      const itemIndex = newValue.index.index

      /** Define o valor max como ValorMax - 1 */
      const customMax = this.hotelMaxMonth

      /** Define mostrar o item como dafault */
      this.showMaxMonths = true

      /** Verifica o valor do index para alterar label e valor do inputRadio */
      if (itemIndex === customMax - 4) {
        this.maxMonthsLabel = '5 Meses'
        this.MaxMonthValue = 5
        this.period = 1
      } else if (itemIndex === customMax - 3) {
        this.maxMonthsLabel = '4 Meses'
        this.MaxMonthValue = 4
        this.period = 1
      } else if (itemIndex === customMax - 2) {
        this.maxMonthsLabel = '3 Meses'
        this.MaxMonthValue = 3
        this.period = 1
      } else if (itemIndex === customMax - 1) {
        this.maxMonthsLabel = '2 Meses'
        this.MaxMonthValue = 2
        this.period = 1
      } else if (customMax === itemIndex) {
        this.showMaxMonths = false
        this.period = 1
      } else {
        this.maxMonthsLabel = '6 Meses'
        this.MaxMonthValue = 6
        this.period = 1
      }

      /** Faz a chamada dos dados */
      this.getCalendarData()
    },
    period(newValue, oldValue) {
      if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return

      this.getCalendarData()
    }
  },
  beforeCreate() {
    /** Salva os meses máximos do hotel */
    this.$calendar.getMaxMonths().then((response) => {
      this.hotelMaxMonth = response.data.numeroMesesCalendario
    })
  },
  async created() {
    /** Define valores de dados iniciais */
    this.maxMonthsLabel = '6 Meses'
    this.MaxMonthValue = 6

    /** Requisita os dados do hotel  */
    await this.requestHotelConfigs(this.$route.params.id).then((response) => {
      this.name = response.nome
      this.breadcrumb = [
        {
          label: 'Letsbook',
          url: '',
          isSpa: false
        },
        {
          label: 'Hotéis',
          url: '/hoteis',
          isSpa: true
        },
        {
          label: capitalizeString(response.nome),
          url: `/hoteis/editar/${response.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        },
        {
          label: 'Calendário de tarifas e disponibilidade',
          url: `/hoteis/calendario-tarifas-allotment/${response.codigo}`,
          isSpa: true /** se deve ser um link para o site novo ou antigo */
        }
      ]

      /** Se for tarifa absolute busca as opções de tarifas */
      if (response.tarifarioAbsoluto) {
        this.loadBaseRates()
      }
    })

    /** Busca as opções de quartos */
    await this.$room.get(this.hotelId).then((response) => {
      this.rooms = response.data.filter((item) => item.ativo)

      const obj = {
        ativo: true,
        base: true,
        codigo: 0,
        value: 0,
        nome: 'Todos'
      }

      this.rooms.splice(0, 0, obj)

      /** Seta o primeiro item como o valor inicial */
      try {
        this.selectedRoom = {
          text: capitalizeString(this.rooms[0].nome),
          value: this.rooms[0].codigo
        }
      } catch (e) {
        // eslint-disable-next-line
        console.error(e)
      }
    })

    this.getCalendarData()
  },
  methods: {
    /**
     * Busca as informações dos quartos do hotel
     * para a data passada
     *
     * @param {Date} date - Data para buscar os dados de allotment
     * @param {Object} dayData - Dados relacionados ao dia selecionado
     */
    getDayEditionData(date, dayData) {
      this.editionModalCurrentData.title = formatDate(date, 'dd MMMM yyyy')
      this.editionModalCurrentData = {
        ...this.editionModalCurrentData,
        ...dayData
      }

      this.showEditDayModal = true
    },
    /**  Busca os dados de allotment para o filtro selecionado */
    getCalendarData() {
      /** Se já existe uma requisição na fila, cancela */
      if (this.filterRequest) {
        clearTimeout(this.filterRequest)
      }

      this.loading = true

      if (+this.selectedRoom.value !== 0) {
        /** Cria uma nova espera para requisitar */
        this.filterRequest = setTimeout(() => {
          this.$calendar
            .getCalendar({
              quarto: +this.selectedRoom.value,
              tarifa: this.rate.value,
              quantidadePax: +this.ocupation.value.quantity,
              tipoPax: +this.ocupation.value.type,
              mes: +this.startPeriod.value.month,
              ano: +this.startPeriod.value.year,
              numeroMeses: +this.period
            })
            .then((response) => {
              /** Padroniza e guarda os dados para o calendário */
              this.calendarData = response.data.map((item) => {
                const obj = {
                  customData: {
                    type: 'full',
                    guest: this.ocupation.text /** Ocupação filtrada */,
                    price: this.$options.filters[this.getCurrency](
                      item.valor
                    ) /** Valor calculado com o diferencial */,
                    discount: item.desconto /** Valor do desconto */,
                    priority: item.prioridadeLB,
                    closed: +item.fechado /** Se está fechado para vendas. Converte o Boolean para Integer */,
                    showDetail: false /** Mostra a tooltip de detalhes */,
                    disponibility:
                      item.quantidadeDisponivel /** Disponibilidade */,
                    usedQuantity:
                      item.quantidadeUtilizada /** Quantidade usada */,
                    allotment:
                      item.quantidadeAllotment /** Quantidade de allotments */,
                    cta: item.cta /** Fechado para checkin */,
                    ctd: item.ctd /** Fechado para checkout */,
                    minNights: item.minimoNoites /** Mínimo de noites */,
                    minNightsType:
                      item.tipoMinimoNoites /** Tipo de mínimo de noites. Passando por ou checkin */,
                    date: item.data /** Data pesquisada */,
                    paxPrices:
                      item.valoresPax /** Preços de todas as ocupações */
                  },
                  dates: new Date(item.data)
                }

                return obj
              })

              /**
               * Valida a forma de apresentação do calendário
               * Normal ou comprimido
               */
              if (this.oneMonth) {
                this.compressCalendarHeight = false
              } else {
                this.compressCalendarHeight = true
              }

              this.loading = false
            })
            .catch((error) => {
              this.$bus.$emit('show-alert-chip', {
                message: error.messages
              })
            })

          clearTimeout(this.filterRequest)
          this.filterRequest = null
        }, this.filterRequestDelay)
      } else {
        /** Cria uma nova espera para requisitar */
        this.filterRequest = setTimeout(() => {
          this.$calendar
            .getCalendarResume({
              codigoHotel: this.$route.params.id,
              mes: +this.startPeriod.value.month,
              ano: +this.startPeriod.value.year,
              numeroMeses: +this.period
            })
            .then((response) => {
              /** Padroniza e guarda os dados para o calendário */
              this.calendarData = response.data.map((item) => {
                const obj = {
                  customData: {
                    type: 'resume',
                    status: item.status,
                    prioridade: item.quartosPrioridade,
                    restricao: item.quartosRestricao,
                    showDetail: false /** Mostra a tooltip de detalhes */,
                    disponibility:
                      item.quantidadeDisponivel /** Disponibilidade */,
                    usedQuantity:
                      item.quantidadeUtilizada /** Quantidade usada */,
                    allotment:
                      item.quantidadeAllotment /** Quantidade de allotments */,
                    date: item.data /** Data pesquisada */
                  },
                  dates: new Date(item.data)
                }

                return obj
              })

              /**
               * Valida a forma de apresentação do calendário
               * Normal ou comprimido
               */
              if (this.oneMonth) {
                this.compressCalendarHeight = false
              } else {
                this.compressCalendarHeight = true
              }

              this.loading = false
            })
            .catch((error) => {
              this.$bus.$emit('show-alert-chip', {
                message: error.messages
              })
            })

          clearTimeout(this.filterRequest)
          this.filterRequest = null
        }, this.filterRequestDelay)
      }
    },
    /**
     * Atualiza a diária editada e salva no calendário
     * @param {Object} dailyObj - objeto com os valores atualizados na edição
     */
    updateDaily(dailyObj) {
      /**
       * Armazena os valores do quarto editado caso seja
       * igual ao selecionado no filtro principal
       */
      const currentUpdatedRoom = dailyObj.quartos.find(
        (room) => room.codigoQuarto === this.selectedRoom.value
      )

      /** Se é igual */
      if (currentUpdatedRoom) {
        /** Pega a posição do dia que deve ser atualizado */
        const dayIndex = this.calendarData.findIndex(
          (day) =>
            day.dates.getTime() === new Date(currentUpdatedRoom.data).getTime()
        )

        let discount = currentUpdatedRoom.desconto

        if (dayIndex > -1) {
          let price = currentUpdatedRoom.valor
          const isAnOldDate =
            new Date(currentUpdatedRoom.data).getTime() <
            new Date().setHours(0, 0, 0, 0)

          /**
           * Se é tarifa absoluta pega o valor
           * do pax selecionado
           */
          if (this.isAbsoluteRate) {
            price = currentUpdatedRoom.valoresPax.find(
              (item) =>
                +item.quantidadePax === +this.ocupation.value.quantity &&
                +item.tipoPax === this.ocupation.value.type
            )?.valor
          } else {
            /** Se é tarifa flutuante pega o desconto fixo */
            discount = dailyObj.quartos[0].desconto ?? null
          }

          this.calendarData[dayIndex].customData = {
            ...this.calendarData[dayIndex].customData,
            price: this.$options.filters[this.getCurrency](price),
            discount: discount,
            priority: currentUpdatedRoom.prioridadeLB,
            closed: +currentUpdatedRoom.fechado,
            disponibility: isAnOldDate
              ? 0
              : currentUpdatedRoom.quantidadeDisponivel /** Adiciona a quantidade de allotment a disponibilidade */,
            usedQuantity: currentUpdatedRoom.quantidadeUtilizada,
            allotment: currentUpdatedRoom.quantidadeAllotment,
            cta: currentUpdatedRoom.cta,
            ctd: currentUpdatedRoom.ctd,
            minNights: currentUpdatedRoom.minimoNoites,
            minNightsType: currentUpdatedRoom.tipoMinimoNoites,
            paxPrices: currentUpdatedRoom.valoresPax,
            edited: true /** Se o dia foi editado manualmente */
          }
        }
      }
    },
    /**
     * Carrega as tarifas base do hotel
     */
    async loadBaseRates() {
      if (this.isAbsoluteRate) {
        const response = await this.$rate.getAbsoluteRates(
          this.$route.params.id
        )
        this.baseRates = response.data

        this.ratesOptions = this.baseRates.map((item) => ({
          text: item.nome,
          value: item.codigo
        }))

        if (this.ratesOptions.length > 0 && !this.rate) {
          this.rate = this.ratesOptions[0]
        }
      }
    },
    /**
     * Abre o modal de exclusão de tarifa base
     */
    openDeleteBaseRateModal() {
      this.showDeleteBaseRateModal = true
    },
    /**
     * Confirma a exclusão das tarifas base selecionadas
     * @param {Array<String>} selectedRates - Array com os códigos das tarifas selecionadas para exclusão.
     */
    async confirmDeleteBaseRate(selectedRates) {
      try {
        await this.$rate.excluirTarifasAbsolutas({
          hotel: this.$route.params.id,
          tarifas: selectedRates
        })

        this.$bus.$emit('show-alert-chip', {
          message: 'Tarifa base excluída com sucesso!'
        })

        await this.loadBaseRates()
        this.getCalendarData()
      } catch (error) {
        this.$bus.$emit('show-alert-chip', {
          message: error.messages || 'Erro ao excluir tarifas'
        })
      } finally {
        /** Fecha o modal após sucesso ou erro */
        this.$refs.deleteBaseRateModal.finishDelete()
      }
    },
    ...mapActions('hotel', ['requestHotelConfigs'])
  }
}
</script>

<style lang="scss" scoped>
::v-deep .lb-breadcrumb--link:nth-child(2) {
  cursor: default;
  pointer-events: none;
}
.allotment-page {
  .radio-fields {
    display: flex;

    &--input {
      margin-right: 15px;
    }
  }

  .legend {
    display: flex;
    align-items: center;
    &:before {
      display: inline-block;
      width: 31px;
      height: 2px;
      margin-right: 5px;
      content: '';
      background-color: pmw-colors(success-100);
    }

    &__closed {
      &::before {
        background-color: pmw-colors(error-100);
      }
    }
    &__parcial-closed {
      &::before {
        background-color: pmw-colors(orange-100);
      }
    }
  }

  .circle-legend {
    display: flex;
    align-items: center;
    &:before {
      height: 18px;
      width: 18px;
      border-radius: 50%;
      display: inline-block;
      margin-right: 5px;
      content: '';
      background-color: pmw-colors(success-100);
    }

    &__closed {
      &::before {
        background-color: pmw-colors(error-100);
      }
    }
    &__parcial-closed {
      &::before {
        background-color: pmw-colors(orange-100);
      }
    }
  }

  .col {
    &__self-end {
      display: flex;
      justify-content: flex-end;
    }
  }

  .optional-filters {
    &--icon {
      color: $gray-dark;

      &:hover {
        color: $blue;
      }
    }
  }

  .options-section {
    padding: {
      top: 20px;
      left: 20px;
      right: 20px;
    }

    & + .options-section {
      border-top: 1px solid $gray-border;
    }

    &--option {
      margin-bottom: 20px;
      white-space: nowrap;
    }
  }

  &-sidebar {
    &--hotel {
      background-color: pmw-colors('blue-100');
      height: 85px;

      &-text {
        color: $pmw-white;
        text-align: center;
      }
    }

    &--menu {
      margin-top: -20px;
      max-height: calc(100vh - 280px);
      overflow-y: scroll;
    }

    &--actions {
      padding-top: 10;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }
  }

  &--description {
    margin-top: 43px;
  }

  &--input-max {
    height: 50px;
    padding: 10px 15px;
    border-top: solid 1px $gray-border;
    margin-top: 10px;
  }

  &--max-months {
    color: pmw-colors('blue-100');
    margin-top: 24px;
  }

  ::v-deep {
    .content--white_card {
      max-width: min-content;
    }
  }

  .lowercase {
    text-transform: lowercase;
    &::first-letter {
      text-transform: uppercase;
    }

    &--input-max {
      height: 50px;
      padding: 10px 15px;
      border-top: solid 1px $gray-border;
      margin-top: 10px;
    }

    &--max-months {
      color: pmw-colors('blue-100');
      margin-top: 24px;
    }
  }
}
</style>
